import { I18n } from '@calendly/i18n'

import { setCheckoutError, stopPurchase } from 'features/billing/checkout_info_slice'

/**
 * Utility functions for PayPal Payment Session error handling
 *
 * This module centralizes common error patterns and provides consistent error messages
 * for the PayPal Payment Session integration. It includes both specific
 * error dispatchers for different scenarios and generic error handlers.
 *
 * @module paypal_error_utils
 */

/**
 * Dispatches a PayPal authentication error
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} message - Error message to display (will be made more specific if generic)
 */
export const dispatchPaypalAuthError = (dispatch, message) => {
  // Improve generic error messages with more actionable context
  let errorMessage = message
  if (message === 'missing token' || message.toLowerCase().includes('missing token')) {
    errorMessage = 'PayPal authentication token is missing. Please try the payment process again.'
  } else if (!message || message.trim() === '') {
    errorMessage = 'PayPal authentication failed. Please try again or use a different payment method.'
  }

  dispatch(setCheckoutError({
    type: 'payment_method',
    message: I18n.t('errors.billing.paypal.authentication_failed', { message: errorMessage })
  }))
}

/**
 * Dispatches a PayPal payment completion error
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} message - Error message to display (will be made more specific if generic)
 */
export const dispatchPaypalPaymentError = (dispatch, message) => {
  // Improve generic error messages with more actionable context
  let errorMessage = message
  if (message === 'Please try again.' || !message || message.trim() === '') {
    errorMessage = I18n.t('errors.billing.paypal.unexpected_error', {
      default: 'An unexpected error occurred. Please try again.'
    })
  } else if (message.toLowerCase().includes('network') || message.toLowerCase().includes('timeout')) {
    errorMessage = 'Network error occurred. Please check your connection and try again.'
  } else if (message.toLowerCase().includes('token') || message.toLowerCase().includes('invalid')) {
    errorMessage = 'Payment session expired. Please start the payment process again.'
  }

  dispatch(setCheckoutError({
    type: 'payment_method',
    message: I18n.t('errors.billing.paypal.payment_failed', { message: errorMessage })
  }))
}

/**
 * Dispatches a PayPal initiation error
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} message - Error message to display
 */
export const dispatchPaypalInitiationError = (dispatch, message) => {
  let errorMessage = message
  if (!message || message.trim() === '') {
    errorMessage = I18n.t('errors.billing.paypal.unexpected_error', {
      default: 'An unexpected error occurred. Please try again.'
    })
  }

  dispatch(setCheckoutError({
    type: 'payment_method',
    message: I18n.t('errors.billing.paypal.initiation_failed', { message: errorMessage })
  }))
}

/**
 * Dispatches a PayPal success message
 * Note: This currently uses setCheckoutError for display, which is semantically incorrect
 * but maintains compatibility with existing UI. Consider refactoring to use a proper
 * success/notification system in the future.
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} message - Success message to display
 */
export const dispatchPaypalSuccess = (dispatch, message) => {
  // For informational messages like "popup opened", we don't need to show them
  // The loading state on the button is sufficient feedback
  if (message.includes('authentication opened') || message.includes('opened')) {
    // Don't show message - loading state is enough feedback
    return
  }

  // Map success message types to translation keys
  // Only show success messages that are important (not just "popup opened")
  if (message.includes('authentication successful')) {
    dispatch(setCheckoutError({
      type: 'payment_method',
      message: I18n.t('errors.billing.paypal.success_authentication')
    }))
  } else if (message.includes('payment completed')) {
    // Payment completed should navigate away, so we might not need to show this
    // But keep it for now in case navigation is delayed
    dispatch(setCheckoutError({
      type: 'payment_method',
      message: I18n.t('errors.billing.paypal.success_payment')
    }))
  } else if (!message.includes('opened')) {
    // Fallback for any other success messages (but not "popup opened")
    dispatch(setCheckoutError({
      type: 'payment_method',
      message: I18n.t('errors.billing.paypal.success_authentication')
    }))
  }
}

/**
 * Dispatches a PayPal cancellation error
 * @param {Function} dispatch - Redux dispatch function
 */
export const dispatchPaypalCancellation = (dispatch) => {
  dispatch(setCheckoutError({
    type: 'payment_method',
    message: I18n.t('errors.billing.paypal.authorization_failed')
  }))
}

/**
 * Dispatches a PayPal popup blocked error
 * @param {Function} dispatch - Redux dispatch function
 */
export const dispatchPaypalPopupBlocked = (dispatch) => {
  dispatch(setCheckoutError({
    type: 'payment_method',
    message: I18n.t('errors.billing.paypal.popup_blocked')
  }))
}

/**
 * Dispatches a PayPal invalid response error
 * @param {Function} dispatch - Redux dispatch function
 */
export const dispatchPaypalInvalidResponse = (dispatch) => {
  dispatch(setCheckoutError({
    type: 'payment_method',
    message: I18n.t('errors.billing.paypal.invalid_response')
  }))
}

/**
 * Handles PayPal errors with automatic purchase stopping
 *
 * This is the main error handler for PayPal operations that should stop the purchase
 * process. It automatically dispatches the appropriate error message and calls stopPurchase().
 *
 * @param {Function} dispatch - Redux dispatch function
 * @param {Error} error - The error object containing the error message
 * @param {string} operation - The operation that failed ('authentication', 'payment', 'initiation')
 *
 * @example
 * try {
 *   await somePaypalOperation()
 * } catch (error) {
 *   handlePaypalError(dispatch, error, 'authentication')
 * }
 */
export const handlePaypalError = (dispatch, error, operation) => {
  const message = error.message || `${operation} failed`

  if (operation === 'authentication') {
    dispatchPaypalAuthError(dispatch, message)
  } else if (operation === 'payment') {
    dispatchPaypalPaymentError(dispatch, message)
  } else {
    dispatchPaypalInitiationError(dispatch, message)
  }

  dispatch(stopPurchase())
}

/**
 * Creates a standardized error response for PayPal actions
 *
 * All PayPal actions should return [success, data] tuples where:
 * - success: boolean indicating if operation succeeded
 * - data: object with standardized structure
 *
 * @param {string} message - Error message to display to user
 * @param {string} operation - The operation that failed ('authentication' | 'payment' | 'initiation')
 * @param {string} [code] - Optional error code for debugging
 * @param {Error} [originalError] - Optional original error object
 * @returns {Array<boolean, Object>} Tuple of [false, errorResponse]
 *
 * @example
 * return createPaypalErrorResponse('Payment failed', 'payment', 'PAYMENT_FAILED', error)
 */
export const createPaypalErrorResponse = (message, operation, code, originalError) => [false, {
  success: false,
  error: {
    message,
    code: code || 'UNKNOWN_ERROR',
    operation,
    originalError: originalError?.message
  }
}]

/**
 * Creates a standardized success response for PayPal actions
 *
 * @param {Object} data - Success data to return
 * @returns {Array<boolean, Object>} Tuple of [true, successResponse]
 *
 * @example
 * return createPaypalSuccessResponse({ tokenId, popup })
 */
export const createPaypalSuccessResponse = (data) => [true, {
  success: true,
  data
}]
